// third-party
import { createSlice } from "@reduxjs/toolkit";
import { dispatch } from "store";

// ==============================|| SLICE - KANBAN ||============================== //

const initialState = {
  error: null,
  editor: {
    title: "",
    description: "",
    callId: "",
    elements: [],
    selectedNode: [],
    edges: [],
    mode: "design" // "design" or "view" mode
  }
};

const ussdStore = createSlice({
  name: "ussd",
  initialState,
  reducers: {
    // HAS ERROR
    hasError(state, action) {
      state.error = action.payload;
    },

    //set Title
    setTitle(state, action) {
      state.editor.title = action.payload.title ?? state.editor.title;
    },
    setCallId(state, action) {
      state.editor.callId = action.payload.callId ?? state.editor.callId;
    },

    //set Description
    setDescription(state, action) {
      state.editor.description = action.payload.description ?? state.editor.description;
    },

    // Set Editor State
    setEditorState(state, action) {
      state.editor.elements = action.payload.elements ?? state.editor.elements;
      state.editor.edges = action.payload.edges ?? state.editor.edges;
    },

    // Load Editor Data
    setElements(state, action) {
      state.editor.elements = action.payload.elements || state.editor.elements;
      state.editor.edges = action.payload.edges || state.editor.edges;
    },

    // Select Element
    selectElement(state, action) {
      state.editor.selectedNode = action.payload.element;
    },

    //delete element
    deleteElement(state, action) {
      const deletedElement = action.payload.element;
      state.editor.elements = state.editor.elements.filter((element) => element.id !== deletedElement.id);
      state.editor.edges = state.editor.edges.filter((edge) => edge.source !== deletedElement.id && edge.target !== deletedElement.id);
    },

    // delete edge
    deleteEdge(state, action) {
      const deletedEdge = action.payload.edge;
      state.editor.edges = state.editor.edges.filter((edge) => edge.id !== deletedEdge.id);
    },

    //copy element and edges
    copyElement(state, action) {
      const copiedElement = action.payload.element;
      const newElement = {
        ...copiedElement,
        id: Math.random(),
        position: {
          x: copiedElement.position.x + 50,
          y: copiedElement.position.y + 50
        }
      };
      state.editor.elements = [...state.editor.elements, newElement];
      const copiedEdges = state.editor.edges.filter((edge) => edge.source === copiedElement.id || edge.target === copiedElement.id);
      copiedEdges.forEach((edge) => {
        const newEdge = {
          ...edge,
          id: Math.random(),
          source: edge.source === copiedElement.id ? newElement.id : edge.source,
          target: edge.target === copiedElement.id ? newElement.id : edge.target
        };
        state.editor.edges = [...state.editor.edges, newEdge];
      });
    },

    //Delete selected elements
    deleteSelectedElements(state) {
      const selectedElements = state.editor.selectedNode;
      selectedElements.forEach((element) => {
        state.editor.elements = state.editor.elements.filter((node) => node.id !== element.id);
        state.editor.edges = state.editor.edges.filter((edge) => edge.source !== element.id && edge.target !== element.id);
      });
      state.editor.selectedNode = [];
    },

    //duplicate element editor state
    duplicateElement(state) {
      const nodes = state.editor.selectedNode;
      const newElements = [...state.editor.elements];
      let newNodes = [];
      nodes.forEach((node, index) => {
        const newNode = {
          ...node,
          id: `${node.id.split("-")[0]}-${newElements.length + (index + 1)}`, // Create a new ID
          position: { x: node.position.x + 80, y: node.position.y + 50 }
        };
        newElements.push(newNode);
        newNodes.push(newNode);
      });
      state.editor.selectedNode = newNodes;
      state.editor.elements = newElements;
    },

    // Toggle between design and view modes
    setEditorMode(state, action) {
      state.editor.mode = action.payload.mode;
    }
  }
});

export const {
  setDescription,
  setTitle,
  setCallId,
  duplicateElement,
  deleteSelectedElements,
  copyElement,
  setElements,
  loadData,
  save,
  selectElement,
  deleteElement,
  deleteEdge,
  setEditorMode
} = ussdStore.actions;

// Reducer
export default ussdStore.reducer;

export const saveFlow = (state) => {
  return new Promise((resolve, reject) => {
    try {
      const elements = state.editor.elements;
      const edges = state.editor.edges;

      // Convert the flow data to JSON format
      const flowData = {
        elements: elements.map((element) => ({
          id: element.id,
          data: element.data,
          type: element.type,
          position: element.position
        })),
        edges: edges.map((edge) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle,
          targetHandle: edge.targetHandle
        }))
      };

      // Convert to JSON string
      const jsonString = JSON.stringify(flowData, null, 2);

      // Create a Blob with the JSON data
      const blob = new Blob([jsonString], { type: "application/json" });

      // Create a link element
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "valid.json";

      // Trigger the download
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      resolve("Flow saved to valid.json successfully!");
    } catch (error) {
      reject(new Error("Failed to save flow: " + error.message));
    }
  });
};

export const loadFlow = () => {
  const data = JSON.parse(localStorage.getItem("ussdflow"));
  dispatch(setElements({ elements: data.elements, edges: data.edges }));
};

//Convert flow to JSON Tree
export const publishFlow = async (state) => {
  const nodes = state.editor.elements;
  return new Promise((resolve, reject) => {
    const getNode = (id) => {
      let n = { ...nodes.find((node) => node.id === id) };
      delete n.position;
      delete n.positionAbsolute;
      delete n.selected;
      delete n.dragging;
      delete n.height;
      delete n.width;
      return n;
    };
    // Create a mapping of each node's children
    const nodeMap = {};
    // Populate the nodeMap with children arrays
    state.editor.edges.forEach((edge) => {
      if (!nodeMap[edge.source]) {
        nodeMap[edge.source] = [];
      }
      nodeMap[edge.source].push(edge.target);
    });
    // Recursively build the node hierarchy
    const buildHierarchy = (nodeId) => {
      const node = { ...getNode(nodeId), children: [] };
      if (nodeMap[nodeId]) {
        //check if children of node is more than 1
        if (nodeMap[nodeId].length > 1) {
          let prevChild = null;
          node.children = nodeMap[nodeId].map((childId) => {
            if (!prevChild) {
              prevChild = childId;
              return buildHierarchy(childId);
            } else {
              //check if prevChild order is same as current
              if (getNode(prevChild).data.order === getNode(childId).data.order) {
                //reject(`Order ID of the children of Node("${getNode(nodeId).data.label}") must be unique`)
                reject(`Order ID of the children of a parent Node must be unique`);
              } else {
                return buildHierarchy(childId);
              }
            }
          });
        } else {
          node.children = nodeMap[nodeId].map((childId) => buildHierarchy(childId));
        }
      }
      return node;
    };

    // Start building the hierarchy from the root node "start-0"
    const result = { nodes: [buildHierarchy("start-0")] };
    resolve(result);
  });
};
